import { useCallback, useEffect, useState } from 'react';

export const THEME_STORAGE_KEY = 'opendate.theme';

const readPref = () => {
  try { return window.localStorage.getItem(THEME_STORAGE_KEY) || 'system'; }
  catch (e) { return 'system'; }
};
const readSystemDark = () => {
  try { return !!(window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches); }
  catch (e) { return false; }
};
const resolveTheme = (pref, systemDark) => {
  if (pref === 'light' || pref === 'dark') return pref;
  return systemDark ? 'dark' : 'light';
};

const listeners = new Set();
const notifyAll = () => { listeners.forEach((l) => l()); };

const applyDocumentTheme = () => {
  try {
    document.documentElement.setAttribute('data-theme', resolveTheme(readPref(), readSystemDark()));
  } catch (e) {}
};

const writePref = (next) => {
  try { window.localStorage.setItem(THEME_STORAGE_KEY, next); } catch (e) {}
  applyDocumentTheme();
  notifyAll();
};

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === THEME_STORAGE_KEY) { applyDocumentTheme(); notifyAll(); }
  });
}

const useTheme = () => {
  const [theme, setThemeState] = useState(() => (typeof window !== 'undefined' ? readPref() : 'system'));
  const [systemDark, setSystemDark] = useState(() => (typeof window !== 'undefined' ? readSystemDark() : false));

  useEffect(() => {
    const onChange = () => setThemeState(readPref());
    listeners.add(onChange);
    return () => { listeners.delete(onChange); };
  }, []);

  useEffect(() => {
    const mq = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)');
    if (!mq) return undefined;
    const onChange = (e) => setSystemDark(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange); else mq.addListener(onChange);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', onChange); else mq.removeListener(onChange); };
  }, []);

  const resolvedTheme = resolveTheme(theme, systemDark);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', resolvedTheme);
  }, [resolvedTheme]);

  const setTheme = useCallback((next) => { writePref(next); }, []);

  return { theme, resolvedTheme, setTheme };
};

export default useTheme;
